/**
 * Microphone capture for voice Quick Add, plus the one format that leaves the
 * browser: 16 kHz mono PCM16 WAV.
 *
 * Chrome records webm/opus and Safari records mp4/aac, and OpenRouter accepts
 * neither uniformly — so whatever the browser recorded is decoded with Web Audio
 * and re-encoded as WAV before upload. One path for every browser, including the
 * installed iOS PWA.
 *
 * iOS grants the mic (and lets an AudioContext run) only from a direct user
 * gesture, so `AudioRecorder.start()` must be invoked synchronously inside the
 * tap handler — it calls getUserMedia and creates the AudioContext before its
 * first `await`.
 */

const RECORDER_MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
const TARGET_SAMPLE_RATE = 16_000;

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') {
    return undefined;
  }
  return RECORDER_MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type));
}

export class AudioRecorder {
  private readonly chunks: Blob[] = [];
  private readonly analyserData: Float32Array<ArrayBuffer>;
  private stopped = false;

  private constructor(
    private readonly stream: MediaStream,
    private readonly context: AudioContext,
    private readonly analyser: AnalyserNode,
    private readonly recorder: MediaRecorder,
  ) {
    this.analyserData = new Float32Array(analyser.fftSize);
    recorder.addEventListener('dataavailable', (event) => {
      if (event.data.size > 0) {
        this.chunks.push(event.data);
      }
    });
  }

  /** Call synchronously from a user gesture (see file header). */
  static start(): Promise<AudioRecorder> {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      return Promise.reject(new Error('unsupported'));
    }
    // Both of these must happen inside the gesture's synchronous window.
    const streamPromise = navigator.mediaDevices.getUserMedia({ audio: true });
    const context = new AudioContext();
    void context.resume();

    return streamPromise.then((stream) => {
      const analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      context.createMediaStreamSource(stream).connect(analyser);
      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const instance = new AudioRecorder(stream, context, analyser, recorder);
      recorder.start(250);
      return instance;
    });
  }

  /** Current input loudness (RMS, 0..1) for the live meter and silence detection. */
  level(): number {
    if (this.stopped) {
      return 0;
    }
    this.analyser.getFloatTimeDomainData(this.analyserData);
    let sum = 0;
    for (const sample of this.analyserData) {
      sum += sample * sample;
    }
    return Math.sqrt(sum / this.analyserData.length);
  }

  /** Finish recording and hand back the raw clip in the browser's native format. */
  stop(): Promise<Blob> {
    if (this.stopped) {
      return Promise.resolve(new Blob(this.chunks, { type: this.recorder.mimeType }));
    }
    return new Promise((resolve) => {
      this.recorder.addEventListener(
        'stop',
        () => resolve(new Blob(this.chunks, { type: this.recorder.mimeType })),
        { once: true },
      );
      this.recorder.stop();
      this.release();
    });
  }

  /** Abandon the recording (user closed the modal, switched to typing, etc.). */
  cancel(): void {
    if (this.stopped) {
      return;
    }
    if (this.recorder.state !== 'inactive') {
      this.recorder.stop();
    }
    this.release();
  }

  private release(): void {
    this.stopped = true;
    for (const track of this.stream.getTracks()) {
      track.stop();
    }
    void this.context.close();
  }
}

/** Decode the recorded clip and re-encode it as base64 16 kHz mono PCM16 WAV. */
export async function blobToWavBase64(blob: Blob): Promise<string> {
  const encoded = await blob.arrayBuffer();
  // An OfflineAudioContext can decode without a user gesture (unlike a live one on iOS).
  const decoded = await new OfflineAudioContext(1, 1, TARGET_SAMPLE_RATE).decodeAudioData(encoded);
  const frameCount = Math.max(1, Math.ceil(decoded.duration * TARGET_SAMPLE_RATE));
  // Rendering into a 1-channel context at 16 kHz down-mixes and resamples in one go.
  const offline = new OfflineAudioContext(1, frameCount, TARGET_SAMPLE_RATE);
  const source = offline.createBufferSource();
  source.buffer = decoded;
  source.connect(offline.destination);
  source.start();
  const rendered = await offline.startRendering();
  return bytesToBase64(encodeWav(rendered.getChannelData(0), TARGET_SAMPLE_RATE));
}

function encodeWav(samples: Float32Array, sampleRate: number): Uint8Array {
  const dataBytes = samples.length * 2;
  const buffer = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buffer);
  const writeAscii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++) {
      view.setUint8(offset + i, text.charCodeAt(i));
    }
  };
  writeAscii(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  writeAscii(8, 'WAVE');
  writeAscii(12, 'fmt ');
  view.setUint32(16, 16, true); // PCM chunk size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeAscii(36, 'data');
  view.setUint32(40, dataBytes, true);
  let offset = 44;
  for (const sample of samples) {
    const clamped = Math.max(-1, Math.min(1, sample));
    view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
    offset += 2;
  }
  return new Uint8Array(buffer);
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/** Human-readable reason the mic couldn't start, for the modal's notice. */
export function describeMicError(error: unknown): string {
  const name = error instanceof Error ? error.name : '';
  const message = error instanceof Error ? error.message : '';
  if (message === 'unsupported') {
    return 'This browser can’t record audio here — type your request instead.';
  }
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Microphone access was blocked. Allow it in your browser settings, or type instead.';
  }
  if (name === 'NotFoundError') {
    return 'No microphone was found — type your request instead.';
  }
  return 'Couldn’t start the microphone — type your request instead.';
}
